import { Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface RecallEntryButtonProps {
  /** Fired once the user confirms. The parent runs the recall mutation. */
  onConfirm: () => void;
  /** Pending state for the underlying recall mutation. */
  loading?: boolean;
  /**
   * `label` renders an icon + "Recall" (day list); `icon` renders an
   * icon-only trigger (compact week-grid cell popover).
   */
  variant?: 'label' | 'icon';
  className?: string;
}

/**
 * Per-entry "recall" affordance for a submitted entry: a small
 * confirm popover that warns the entry will drop to draft and need
 * resubmission before firing `onConfirm`. Shared by the day list and the
 * week grid so both views present the same gesture. Uses `Popover` (not a
 * shadcn AlertDialog, which this project doesn't install) — same primitive
 * as `RejectReasonPopover`.
 */
export function RecallEntryButton({
  onConfirm,
  loading,
  variant = 'label',
  className,
}: RecallEntryButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* `PopoverTrigger asChild` clobbers this Button's own
            `data-slot="button"` with its own `data-slot="popover-trigger"`
            (Radix Slot lets the outer wrapper's explicit props win), so the
            global `[data-slot='button']` pill-radius rule never matches this
            trigger. Set the radius directly instead of depending on that rule. */}
        <Button
          size={variant === 'icon' ? 'icon-sm' : 'sm'}
          variant="outline"
          disabled={loading}
          className={cn('rounded-full', className)}
          aria-label="Recall entry to draft"
        >
          <Undo2 className="size-3.5" />
          {variant === 'label' ? <span className="ml-1">Recall</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <p className="text-sm font-medium">Recall this entry?</p>
        <p className="mt-1 text-xs text-muted-foreground">
          It returns to draft so you can edit it — you'll need to resubmit for approval.
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            Recall to draft
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
